'use client'

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { getMe, login as apiLogin, logout as apiLogout, signup as apiSignup, type User, type SignupPayload } from './api'

type AuthContextType = {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<{ ok: boolean; message?: string; user?: User | null }>
  signup: (payload: SignupPayload) => Promise<{ ok: boolean; message?: string }>
  logout: () => Promise<void>
  refresh: () => Promise<User | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = async () => {
    const res = await getMe()
    if (res.ok) {
      setUser(res.body.user)
      return res.body.user || null
    } else {
      setUser(null)
      return null
    }
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false))
  }, [])

  const login = async (email: string, password: string) => {
    const res = await apiLogin(email, password)
    if (res.ok) {
      const refreshed = await refresh()
      return { ok: true, user: refreshed }
    }
    let message = 'Erreur de connexion'
    if (res.status === 403) message = 'Veuillez vérifier votre email avant de vous connecter'
    if (res.status === 401) message = 'Identifiants invalides'
    return { ok: false, message }
  }

  const signup = async (payload: SignupPayload) => {
    const res = await apiSignup(payload)
    if (res.ok) {
      const emailSent = (res.body as any)?.emailSent
      const devVerifyUrl = (res.body as any)?.devVerifyUrl
      const emailError = (res.body as any)?.emailError
      let message = (res.body as any)?.message || 'Inscription réussie. Vérifiez votre email.'
      if (emailSent === false && devVerifyUrl) {
        message = `Email non envoyé (SMTP). Utilisez ce lien pour vérifier votre compte : ${devVerifyUrl}`
      } else if (emailSent === false) {
        message = emailError
          ? `Inscription créée mais email non envoyé : ${emailError}. Contactez un admin ou réessayez plus tard.`
          : 'Inscription créée, mais email non envoyé (SMTP). Contactez un admin ou réessayez plus tard.'
      }
      return { ok: true, message }
    }
    const apiError = (res.body as any)?.error
    return {
      ok: false,
      message:
        apiError ||
        (res.status === 409 ? 'Email déjà enregistré' : res.status === 400 ? 'Données invalides, vérifiez les champs' : 'Impossible de créer le compte'),
    }
  }

  const logout = async () => {
    setUser(null)
    setLoading(false)
    try {
      await apiLogout()
    } catch {
      // ignore network errors on logout
    }
  }

  const value = useMemo(() => ({ user, loading, login, signup, logout, refresh }), [user, loading])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
